/**
 * ScheduledRideCard — single upcoming scheduled ride entry shown in the
 * "Booked for later" list on the passenger landing page.
 *
 * iter-79 decomposition slice 5 of Passenger.jsx.
 *
 * Strict isolation contract (matches Slices 1-4 playbook):
 *   - Zero behavioural change vs. the original inline definition.
 *   - All props, helpers, and rendering logic preserved exactly.
 *   - No dispatch / state-machine / janitor / offer-routing logic touched.
 *
 * Props:
 *   ride     — scheduled ride object (status === "scheduled")
 *   onCancel — () => void
 */
import React from "react";
import { Calendar, X } from "lucide-react";

/** Card for a single upcoming scheduled ride. Shows pickup→drop-off, the
 *  formatted scheduled time, locked-in quote, and a cancel button. */
export const ScheduledRideCard = ({ ride, onCancel }) => {
  const dt = new Date(ride.scheduled_for);
  const dateLabel = dt.toLocaleString("en-GB", {
    weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return (
    <div className="border border-border bg-secondary/20 p-3" data-testid={`scheduled-ride-${ride.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <Calendar className="w-3 h-3 text-[#002FA7]" />
            <span className="label-mini text-[#002FA7] font-mono-tab" data-testid={`scheduled-time-${ride.id}`}>{dateLabel}</span>
          </div>
          <div className="text-xs truncate"><span className="font-semibold">{ride.pickup_address}</span> → {ride.dropoff_address}</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-heading text-lg font-black">£{ride.estimated_fare}</div>
          <div className="label-mini text-muted-foreground">{ride.distance_mi ?? ride.distance_km} mi</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="text-xs px-2 py-1 border border-border hover:bg-destructive hover:text-white hover:border-destructive transition-colors flex items-center gap-1"
        data-testid={`cancel-scheduled-${ride.id}`}
      >
        <X className="w-3 h-3" /> Cancel
      </button>
    </div>
  );
};

export default ScheduledRideCard;
